import type { JSX } from 'react';
import { Fragment, type ReactNode } from 'react';

/** Markdown-lite: **bold**, `code`, bullet and numbered lists, headings and line breaks. No HTML injection. */
function renderInline(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`|\*[^*\n]+\*)/g;
  let last = 0;
  let i = 0;
  for (const m of text.matchAll(re)) {
    const idx = m.index ?? 0;
    if (idx > last) out.push(<Fragment key={`${keyPrefix}-t${i++}`}>{text.slice(last, idx)}</Fragment>);
    const tok = m[0];
    if (tok.startsWith('**')) out.push(<strong key={`${keyPrefix}-b${i++}`}>{tok.slice(2, -2)}</strong>);
    else if (tok.startsWith('`')) out.push(<code key={`${keyPrefix}-c${i++}`}>{tok.slice(1, -1)}</code>);
    else out.push(<em key={`${keyPrefix}-e${i++}`}>{tok.slice(1, -1)}</em>);
    last = idx + tok.length;
  }
  if (last < text.length) out.push(<Fragment key={`${keyPrefix}-t${i++}`}>{text.slice(last)}</Fragment>);
  return out;
}

type Block = { kind: 'p'; lines: string[] } | { kind: 'ul' | 'ol'; items: string[] } | { kind: 'h'; level: number; text: string };

function parseBlocks(markdown: string): Block[] {
  const blocks: Block[] = [];
  let current: Block | undefined;
  const flush = (): void => {
    if (current) blocks.push(current);
    current = undefined;
  };
  for (const raw of markdown.replace(/\r\n/g, '\n').split('\n')) {
    const line = raw.trimEnd();
    if (!line.trim()) {
      flush();
      continue;
    }
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    const ul = /^\s*[-*•]\s+(.*)$/.exec(line);
    const ol = /^\s*\d+[.)]\s+(.*)$/.exec(line);
    if (h) {
      flush();
      blocks.push({ kind: 'h', level: h[1]?.length ?? 1, text: h[2] ?? '' });
    } else if (ul) {
      if (current?.kind !== 'ul') {
        flush();
        current = { kind: 'ul', items: [] };
      }
      current.items.push(ul[1] ?? '');
    } else if (ol) {
      if (current?.kind !== 'ol') {
        flush();
        current = { kind: 'ol', items: [] };
      }
      current.items.push(ol[1] ?? '');
    } else {
      if (current?.kind !== 'p') {
        flush();
        current = { kind: 'p', lines: [] };
      }
      current.lines.push(line);
    }
  }
  flush();
  return blocks;
}

export function Markdown({ text, className }: { text: string; className?: string }): JSX.Element {
  const blocks = parseBlocks(text);
  return (
    <div className={className ? `md ${className}` : 'md'}>
      {blocks.map((b, bi) => {
        if (b.kind === 'h') {
          const Tag = (`h${Math.min(4, b.level + 2)}` as 'h3' | 'h4' | 'h5' | 'h6');
          return <Tag key={bi}>{renderInline(b.text, `h${bi}`)}</Tag>;
        }
        if (b.kind === 'p') {
          return (
            <p key={bi}>
              {b.lines.map((l, li) => (
                <Fragment key={li}>
                  {li > 0 ? <br /> : null}
                  {renderInline(l, `${bi}-${li}`)}
                </Fragment>
              ))}
            </p>
          );
        }
        const Tag = b.kind;
        return (
          <Tag key={bi}>
            {b.items.map((it, ii) => (
              <li key={ii}>{renderInline(it, `${bi}-${ii}`)}</li>
            ))}
          </Tag>
        );
      })}
    </div>
  );
}
